import CustomModal from "@components/common/CustomModal";
import { updateValidationDocs } from "@components/Delivery/DeliveryDetails/Querys";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Container, Typography } from "@mui/material";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import type { Delivery, Doc } from "@types_def/models/delivery.types";
import React, { useEffect } from "react";
import type { FieldPath } from "react-hook-form";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

import { Upload, UploadDropzone, UploadFormItem } from "@/components/common/upload";
import { getDeliveryDetailQueryKey } from "@/components/Delivery/cache";
import { useFindDeliveryByIdQuery } from "@/libs/generated/hooks";

const validationDocsSchema = z.object({
  id: z.number(),
  validationDocs: z.array(
    z.object({
      name: z.string(),
      key: z.string().optional(),
      url: z.string(),
      deleted: z.boolean().optional(),
    })
  ),
});

type ValidationDocsFormData = {
  id: number;
  validationDocs: Doc[];
};

type Props = {
  data: Delivery;
  revalidate?: () => void;
};

function UpdateValidationDocs({ data, revalidate }: Props) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const queryClient = useQueryClient();
  const { refetch: refetchDelivery } = useFindDeliveryByIdQuery(
    { GetByIdInput: { id: data.id } },
    { enabled: false } // We control when to fetch
  );

  const methods = useForm<ValidationDocsFormData>({
    reValidateMode: "onChange",
    resolver: zodResolver(validationDocsSchema),
    defaultValues: {
      id: data.id,
      validationDocs: data.validationDocs ?? [],
    },
  });

  useEffect(() => {
    methods.reset({
      id: data.id,
      validationDocs: data.validationDocs ?? [],
    });
  }, [data.id, data.validationDocs, methods]);

  useWatch({ control: methods.control, name: "validationDocs" });
  const isFetching =
    useIsFetching({ queryKey: getDeliveryDetailQueryKey(data.id), exact: true }) > 0;

  // Add a local state for validationDocsData to force update after delete/add/edit
  const [validationDocsData, setValidationDocsData] = React.useState<Doc[]>(
    (data.validationDocs?.length ?? 0) > 0 ? data.validationDocs : []
  );

  // Update local state when data.validationDocs changes (initial load or refetch)
  useEffect(() => {
    // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
    setValidationDocsData(data.validationDocs ?? []);
  }, [data.validationDocs]);

  // Fetch latest docs after delete/add/edit without relying on query invalidation
  const fetchLatestDocs = async (): Promise<void> => {
    const { data: latest } = await refetchDelivery();
    if (
      latest !== null &&
      latest !== undefined &&
      latest.findDeliveryById !== null &&
      latest.findDeliveryById !== undefined &&
      latest.findDeliveryById.validationDocs !== null &&
      latest.findDeliveryById.validationDocs !== undefined
    ) {
      const docs = latest.findDeliveryById.validationDocs as Doc[];
      setValidationDocsData(docs);
    }
  };

  const handleUpdateValidationDocs = async (formData: ValidationDocsFormData) => {
    try {
      setIsSubmitting(true);
      const { id, validationDocs } = formData;
      // Update validation docs
      await updateValidationDocs({ id, validationDocs }, queryClient);
      if (revalidate) revalidate();
      methods.reset({ id: data.id, validationDocs });
      setIsSubmitting(false);
    } catch (_error) {
      setIsSubmitting(false);
    }
  };

  // Call update after upload
  const handleDocsChange = async (newDocs: Doc[]) => {
    await handleUpdateValidationDocs({ id: data.id, validationDocs: newDocs });
    await fetchLatestDocs();
  };
  const handleDocsDelete = async (newDocs: Doc[]): Promise<void> => {
    methods.setValue("validationDocs", newDocs, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    await fetchLatestDocs();
  };

  // Edit handler: always fetch latest docsData before and after edit to avoid stale state
  const handleDocsEdit = async (editedDoc: Doc, index: number): Promise<void> => {
    // Always use the latest docs from the backend for both key and index mapping
    const { data: latestData } = await refetchDelivery();
    const latest =
      latestData !== null && latestData !== undefined
        ? (latestData as { findDeliveryById: { validationDocs: Doc[] } | null }).findDeliveryById
        : null;
    const docsArray =
      latest !== null && latest !== undefined && Array.isArray(latest.validationDocs)
        ? latest.validationDocs
        : [];

    // Find the doc to replace by key, but fallback to index if not found (for new uploads)
    let docIndex = docsArray.findIndex(
      (d) =>
        d.key !== null && d.key !== undefined && d.key === (validationDocsData[index]?.key ?? "")
    );
    if (docIndex === -1) docIndex = index;
    // Defensive: if index is still invalid, do nothing
    if (docIndex < 0 || docIndex >= docsArray.length) return;

    const newDocs: Doc[] = docsArray.map((d, i) => {
      if (i === docIndex) {
        return {
          ...d,
          ...editedDoc,
          name:
            editedDoc.key !== null && editedDoc.key !== undefined
              ? editedDoc.key
              : (d.key ?? d.name),
          key: editedDoc.key !== null && editedDoc.key !== undefined ? editedDoc.key : d.key,
        };
      }
      return d;
    });
    methods.setValue("validationDocs", newDocs, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    await updateValidationDocs({ id: data.id, validationDocs: newDocs }, queryClient);
    await fetchLatestDocs();
  };

  return (
    <Container maxWidth="md" sx={{ padding: 2 }}>
      <CustomModal
        button={
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={isSubmitting || isFetching}
          >
            Modifier Validation Documents
          </Button>
        }
        fullWidth
        title="Modifier les documents de validation"
        confirmText={isSubmitting || isFetching ? "Modification en cours..." : "Modifier"}
        confirmAction={methods.handleSubmit(handleUpdateValidationDocs)}
        disabled={isSubmitting || isFetching}
        color="primary"
        variant="contained"
        size="md"
        noAction={true}
      >
        <FormProvider {...methods}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Ajoutez ou modifiez les documents de validation pour cette livraison.
          </Typography>
          <Upload.Root>
            <UploadDropzone<ValidationDocsFormData>
              meta={{
                folder: `delivery/${data.id.toString()}/docs/validationDocs/`,
                subFolder: String(data.id),
              }}
              name="validationDocs"
              cb={(docs) => {
                void handleDocsChange(docs as Doc[]);
              }}
            />
            <br />
            {validationDocsData.map((doc: Doc, index: number) => (
              <UploadFormItem<ValidationDocsFormData>
                name={`validationDocs.${index}` as FieldPath<ValidationDocsFormData>}
                key={doc.key}
                meta={{
                  folder: `delivery/${data.id.toString()}/docs/validationDocs/`,
                  subFolder: String(data.id),
                }}
                handleDelete={(key: string) => {
                  const newDocs = validationDocsData.filter((doc: Doc) => doc.key !== key);
                  void handleDocsDelete(newDocs);
                }}
                handleEdit={(editedDoc) => {
                  void handleDocsEdit(editedDoc as Doc, index);
                }}
              />
            ))}
          </Upload.Root>
        </FormProvider>
      </CustomModal>
    </Container>
  );
}

export { UpdateValidationDocs };
